/**
 * Cliente do segundo motor nativo: ONNX Runtime.
 *
 * Existe porque o llama.cpp não abre tudo. Ele cobre linguagem e embeddings;
 * classificador de imagem — idade, emoção, gênero — e detecção de objetos são
 * modelos de visão especializados que ninguém converteu para GGUF, e
 * provavelmente ninguém vai converter.
 *
 * Estes já estão publicados em `.onnx` e rodam como estão, sem conversão. O que
 * muda em relação ao que havia não é o formato do modelo: é onde ele roda.
 * Antes era ONNX dentro do WebView, sobre WebAssembly, com teto de
 * endereçamento de 32 bits. Agora é o mesmo `.onnx` no runtime nativo, com a
 * memória do processo.
 *
 * A divisão com o Java é deliberada: lá ficam pixels e tensores, aqui fica o
 * significado. O Java devolve números crus; este módulo aplica softmax e casa
 * índice com rótulo. Assim um classificador novo não custa uma linha de Java —
 * basta o `.onnx` e o `config.json` do modelo.
 */

interface PonteVisao {
  carregarVisao(nomeOuCaminho: string): string;
  descarregarVisao(): void;
  estadoVisao(): string;
  classificarImagem(dataUrl: string, media: number, desvio: number): string;
}

declare global {
  interface Window {
    wuweiAndroidLlm?: Partial<PonteVisao>;
  }
}

function ponte(): PonteVisao | null {
  const p = typeof window !== "undefined" ? window.wuweiAndroidLlm : undefined;
  return p && typeof p.classificarImagem === "function" ? (p as PonteVisao) : null;
}

export function disponivel(): boolean {
  return ponte() !== null;
}

export interface ModeloVisao {
  nome: string;
  /** Nome do arquivo no aparelho. */
  arquivo: string;
  url: string;
  mb: number;
  /** De onde vêm os rótulos. Buscado na hora, e guardado depois. */
  configUrl: string;
  /** Normalização da imagem. Muda por família de modelo: ViT usa 0,5/0,5, a
   *  linhagem ImageNet usa outros números — e errar aqui não dá erro, dá
   *  resposta plausível e errada. */
  media: number;
  desvio: number;
  descricao: string;
}

/** Modelos de visão conferidos. Curto de propósito, como a lista de GGUF. */
export const VISAO_SUGERIDA: ModeloVisao[] = [
  {
    // Não é classificador: marca 17 pontos do corpo, e quem lê os números é
    // `postura.ts`. Entra nesta lista porque o caminho de baixar e carregar é
    // o mesmo — o que muda é quem interpreta a saída.
    //
    // `media`/`desvio` ficam em 0 de propósito: este modelo recebe inteiro de
    // 0 a 255, e a normalização é ignorada para ele no lado nativo.
    nome: "Postura (MoveNet Lightning)",
    arquivo: "movenet-singlepose-lightning.onnx",
    url: "https://huggingface.co/Xenova/movenet-singlepose-lightning/resolve/main/onnx/model.onnx",
    configUrl: "",
    mb: 9,
    media: 0,
    desvio: 1,
    descricao:
      "Marca ombro, orelha e quadril numa foto e mede a postura. São 9 MB — o menor " +
      "modelo desta lista — e a foto não sai do aparelho.",
  },
  {
    nome: "Estimar idade em foto",
    arquivo: "vit-age-classifier.onnx",
    url: "https://huggingface.co/jdp8/vit-age-classifier/resolve/main/onnx/model_quantized.onnx",
    configUrl: "https://huggingface.co/jdp8/vit-age-classifier/resolve/main/config.json",
    mb: 84,
    media: 0.5,
    desvio: 0.5,
    descricao:
      "O mesmo modelo que rodava na aba, agora no runtime nativo. Eram 229 MB de heap no " +
      "WebAssembly; aqui são 84 MB de arquivo e a memória do processo.",
  },
];

export interface Classe {
  rotulo: string;
  probabilidade: number;
}

/** Carrega um `.onnx`. Devolve "" ou o motivo. */
export function carregar(arquivo: string): string {
  const p = ponte();
  if (!p) return "não há camada nativa neste navegador";
  return p.carregarVisao(arquivo);
}

export function descarregar(): void {
  ponte()?.descarregarVisao();
}

export function estado(): { carregado: boolean; modelo: string | null; lado: number } | null {
  const p = ponte();
  if (!p) return null;
  try {
    return JSON.parse(p.estadoVisao());
  } catch {
    return null;
  }
}

/** Rótulos do modelo, lidos do `config.json` dele no Hugging Face.
 *
 *  Lidos de lá, e não escritos aqui, pelo mesmo motivo de sempre: tabela nossa
 *  envelhece calada. Trocar o modelo por outro com classes diferentes daria
 *  respostas com os nomes antigos — erradas e plausíveis. */
const rotulosPorUrl = new Map<string, string[]>();

export async function rotulos(configUrl: string): Promise<string[]> {
  const guardado = rotulosPorUrl.get(configUrl);
  if (guardado) return guardado;
  const r = await fetch(configUrl);
  if (!r.ok) throw new Error(`não deu para ler os rótulos: ${r.status}`);
  const cfg = (await r.json()) as { id2label?: Record<string, string> };
  const mapa = cfg.id2label ?? {};
  // Ordenado pelo índice numérico: `Object.values` num objeto com chaves "0",
  // "1", "10" devolve em ordem de inserção, e o JSON não garante ordem.
  const lista = Object.keys(mapa)
    .sort((a, b) => Number(a) - Number(b))
    .map((k) => mapa[k]!);
  rotulosPorUrl.set(configUrl, lista);
  return lista;
}

/** Softmax. Com subtração do máximo, que não é elegância: sem ela um logit
 *  grande vira `Infinity` no exponencial e a saída inteira vira `NaN`. */
function softmax(v: number[]): number[] {
  const maior = Math.max(...v);
  const exp = v.map((x) => Math.exp(x - maior));
  const soma = exp.reduce((a, b) => a + b, 0);
  return exp.map((x) => x / soma);
}

/** Classifica uma imagem e devolve as classes mais prováveis. */
export async function classificar(
  dataUrl: string,
  m: ModeloVisao,
  quantas = 3,
): Promise<Classe[]> {
  const p = ponte();
  if (!p) throw new Error("não há camada nativa neste navegador");
  const cru = JSON.parse(p.classificarImagem(dataUrl, m.media, m.desvio)) as number[];
  if (!cru.length) throw new Error("o modelo não devolveu nada — está carregado?");
  const nomes = await rotulos(m.configUrl);
  const probs = softmax(cru);
  return probs
    .map((probabilidade, i) => ({ rotulo: nomes[i] ?? `classe ${i}`, probabilidade }))
    .sort((a, b) => b.probabilidade - a.probabilidade)
    .slice(0, quantas);
}

/** Ponte para os testes. */
declare global {
  interface Window {
    __wuweiVisao: {
      disponivel: typeof disponivel;
      estado: typeof estado;
      VISAO_SUGERIDA: typeof VISAO_SUGERIDA;
    };
  }
}

if (typeof window !== "undefined") {
  window.__wuweiVisao = { disponivel, estado, VISAO_SUGERIDA };
}
